import { z } from "zod";
import { ALL_STATUS_VALUES } from '../constants';
import { isEndDateAfterStartDate } from '../utils/date-formatter';
import { CampaignType } from "@/@types/campaign-roulette";

/**
 * A campanha vira uma linha na PRO015, cuja coluna P15DESCRI é VARCHAR2(40).
 * O back-end prefixa o nome ("Roleta " ou "Chutou Ganhou ") antes de gravar,
 * então o orçamento de caracteres do nome depende do tipo. Espelha
 * `pro015-description.helper.ts` do portal-api (prefixos com espaço incluso).
 */
const PRO015_DESCRIPTION_MAX_LENGTH = 40;
const PRO015_DESCRIPTION_PREFIX: Record<CampaignType, string> = {
  [CampaignType.ROULETTE]: "Roleta ",
  [CampaignType.WORLD_CUP]: "Chutou Ganhou ",
};

function campaignNameBudget(type?: CampaignType): number {
  const prefix =
    PRO015_DESCRIPTION_PREFIX[type ?? CampaignType.ROULETTE] ??
    PRO015_DESCRIPTION_PREFIX[CampaignType.ROULETTE];
  return PRO015_DESCRIPTION_MAX_LENGTH - prefix.length;
}

function validateCampaignNameLength(
  data: { campaignName?: string; campaignType?: CampaignType },
  ctx: z.RefinementCtx,
): void {
  if (!data.campaignName) return;
  const budget = campaignNameBudget(data.campaignType);
  if (data.campaignName.length > budget) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["campaignName"],
      message: `Nome muito grande: máximo ${budget} caracteres para este tipo de campanha.`,
    });
  }
}

const storeSchema = z.object({
  storeCode: z.string().min(1, "Código é obrigatório"),
  storeName: z.string().min(1, "Endereço é obrigatório"),
  nickname: z.string().min(1, "Nome da loja é obrigatório"),
});

const allStoresSchema = z.object({
  storeCode: z.literal("0"),
  storeName: z.string(),
  nickname: z.string(),
});

const flexibleStoreSchema = z.union([storeSchema, allStoresSchema]);

const probabilitySchema = z.object({
  prob: z.preprocess(
    (val) => {
      if (val === null || val === undefined || val === '' || isNaN(Number(val))) {
        return 0;
      }
      return Number(val);
    },
    z.number({
      required_error: "Probabilidade é obrigatória",
      invalid_type_error: "Probabilidade deve ser um número válido"
    })
      .min(0, "Probabilidade deve ser maior ou igual a 0")
      .max(100, "Probabilidade deve ser menor ou igual a 100")
  ),
  prizes: z.preprocess(
    (val) => {
      if (typeof val === 'string') {
        if (val.trim() === '') return [];
        return val.split(',').map(v => {
          const num = parseFloat(v.trim());
          return isNaN(num) ? 0 : num;
        }).filter(n => n > 0);
      }
      return Array.isArray(val) ? val : [];
    },
    z.array(z.number({
      required_error: "Prêmio é obrigatório",
      invalid_type_error: "Prêmio deve ser um número válido"
    }).positive("Prêmio deve ser um valor positivo"), {
      required_error: "Prêmios são obrigatórios",
      invalid_type_error: "Prêmios devem ser números válidos"
    })
      .min(1, "Pelo menos um prêmio deve ser informado")
  ),
});

const baseCampaignRouletteSchema = z.object({
  campaignName: z.string()
    .min(1, "Nome da campanha é obrigatório")
    .max(PRO015_DESCRIPTION_MAX_LENGTH, `Nome da campanha deve ter no máximo ${PRO015_DESCRIPTION_MAX_LENGTH} caracteres`),

  campaignType: z.nativeEnum(CampaignType).default(CampaignType.ROULETTE),

  startDate: z.string()
    .min(1, "Data de início é obrigatória"),
    
  endDate: z.string()
    .min(1, "Data de fim é obrigatória"),
    
  stores: z.array(flexibleStoreSchema)
    .min(1, "Pelo menos uma loja deve ser selecionada")
    .refine((stores) => {
      const codes = stores.map(store => store.storeCode);
      return new Set(codes).size === codes.length;
    }, "Não é possível selecionar a mesma loja mais de uma vez"),
    
  allStoresSelected: z.boolean().optional(),
    
  probabilities: z.array(probabilitySchema)
    .length(6, "Deve ter exatamente 6 probabilidades configuradas")
    .refine((probabilities) => {
      const total = probabilities.reduce((sum, prob) => sum + prob.prob, 0);
      return Math.abs(total - 100) < 0.01;
    }, "A soma das probabilidades deve ser exatamente 100%"),
    
  regulations: z.instanceof(File)
    .optional()
    .refine((file) => {
      if (!file) return true;
      return file.type === "application/pdf";
    }, "Regulamento deve ser um arquivo PDF")
    .refine((file) => {
      if (!file) return true;
      return file.size <= 5 * 1024 * 1024;
    }, "Arquivo deve ter no máximo 5MB"),
    
  banner3x4: z.any({
    required_error: "Banner mobile é obrigatório"
  })
    .refine((file) => file instanceof File, "Banner mobile é obrigatório")
    .refine((file) => file.type.startsWith("image/"), "Banner 3x4 deve ser uma imagem")
    .refine((file) => file.size <= 5 * 1024 * 1024, "Imagem deve ter no máximo 5MB"),
    
  banner16x9: z.any({
    required_error: "Banner desktop é obrigatório"
  })
    .refine((file) => file instanceof File, "Banner desktop é obrigatório")
    .refine((file) => file.type.startsWith("image/"), "Banner 16x9 deve ser uma imagem")
    .refine((file) => file.size <= 5 * 1024 * 1024, "Imagem deve ter no máximo 5MB"),

  linkedCampaignId: z.number().int().positive().optional().nullable(),
});

export const createCampaignRouletteSchema = baseCampaignRouletteSchema
  .superRefine(validateCampaignNameLength)
  .refine((data) => {
    return isEndDateAfterStartDate(data.startDate, data.endDate);
  }, {
    message: "Data de fim deve ser posterior à data de início",
    path: ["endDate"],
  });

export const updateCampaignRouletteSchema = z.object({
  id: z.number().positive("ID da campanha é obrigatório"),
  
  campaignName: z.string()
    .min(1, "Nome da campanha é obrigatório")
    .max(PRO015_DESCRIPTION_MAX_LENGTH, `Nome da campanha deve ter no máximo ${PRO015_DESCRIPTION_MAX_LENGTH} caracteres`)
    .optional(),

  campaignType: z.nativeEnum(CampaignType).optional(),

  startDate: z.string()
    .min(1, "Data de início é obrigatória")
    .optional(),
    
  endDate: z.string()
    .min(1, "Data de fim é obrigatória")
    .optional(),
    
  stores: z.array(flexibleStoreSchema)
    .min(1, "Pelo menos uma loja deve ser selecionada")
    .refine((stores) => {
      const codes = stores.map(store => store.storeCode);
      return new Set(codes).size === codes.length;
    }, "Não é possível selecionar a mesma loja mais de uma vez")
    .optional(),
    
  allStoresSelected: z.boolean().optional(),
    
  probabilities: z.array(probabilitySchema)
    .length(6, "Deve ter exatamente 6 probabilidades configuradas")
    .refine((probabilities) => {
      const total = probabilities.reduce((sum, prob) => sum + prob.prob, 0);
      return Math.abs(total - 100) < 0.01;
    }, "A soma das probabilidades deve ser exatamente 100%")
    .optional(),
    
  regulations: z.instanceof(File)
    .optional()
    .refine((file) => {
      if (!file) return true;
      return file.type === "application/pdf";
    }, "Regulamento deve ser um arquivo PDF")
    .refine((file) => {
      if (!file) return true;
      return file.size <= 5 * 1024 * 1024;
    }, "Arquivo deve ter no máximo 5MB"),
    
  banner3x4: z.any()
    .optional()
    .refine((file) => !file || file instanceof File, "Banner mobile deve ser um arquivo")
    .refine((file) => !file || file.type.startsWith("image/"), "Banner 3x4 deve ser uma imagem")
    .refine((file) => !file || file.size <= 5 * 1024 * 1024, "Imagem deve ter no máximo 5MB"),
    
  banner16x9: z.any()
    .optional()
    .refine((file) => !file || file instanceof File, "Banner desktop deve ser um arquivo")
    .refine((file) => !file || file.type.startsWith("image/"), "Banner 16x9 deve ser uma imagem")
    .refine((file) => !file || file.size <= 5 * 1024 * 1024, "Imagem deve ter no máximo 5MB"),

  linkedCampaignId: z.number().int().positive().optional().nullable(),
}).superRefine(validateCampaignNameLength).refine((data) => {
  if (data.startDate && data.endDate) {
    return isEndDateAfterStartDate(data.startDate, data.endDate);
  }
  return true;
}, {
  message: "Data de fim deve ser posterior à data de início",
  path: ["endDate"],
});

export const campaignRouletteFiltersSchema = z.object({
  search: z.string().optional(),
  status: z.enum(ALL_STATUS_VALUES as [string, ...string[]]).optional(),
  period: z.object({
    start: z.string().optional(),
    end: z.string().optional(),
  }).optional(),
  storeName: z.string().optional(),
});

export type CreateCampaignRouletteFormData = z.infer<typeof createCampaignRouletteSchema>;
export type UpdateCampaignRouletteFormData = z.infer<typeof updateCampaignRouletteSchema>;
export type CampaignRouletteFiltersData = z.infer<typeof campaignRouletteFiltersSchema>;
